import { supabase } from "@/lib/supabase";
import type { Database, Json } from "@/lib/database.types";

export type PreferencesRow = Database["public"]["Tables"]["preferences"]["Row"];
export type PreferencesData = Record<string, Json>;

async function ensurePreferencesRow(userId: string): Promise<PreferencesRow> {
  const { data, error } = await supabase
    .from("preferences")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) throw error;

  if (data) return data;

  const insert = await supabase
    .from("preferences")
    .insert({ user_id: userId, data: {} })
    .select("*")
    .maybeSingle();

  if (insert.error) throw insert.error;
  if (!insert.data) throw new Error("Failed to create preferences row");
  return insert.data;
}

export async function loadPreferences(userId: string): Promise<PreferencesData> {
  const row = await ensurePreferencesRow(userId);
  return (row.data as PreferencesData) ?? {};
}

export async function savePreferences(userId: string, data: PreferencesData): Promise<PreferencesData> {
  const payload: Database["public"]["Tables"]["preferences"]["Update"] = {
    user_id: userId,
    data,
    updated_at: new Date().toISOString(),
  };
  const { data: updated, error } = await supabase
    .from("preferences")
    .upsert(payload)
    .select("*")
    .maybeSingle();
  if (error) throw error;
  return (updated?.data as PreferencesData) ?? {};
}

export async function updatePreferences(
  userId: string,
  updater: (current: PreferencesData) => PreferencesData
): Promise<PreferencesData> {
  const current = await loadPreferences(userId);
  const currentCopy = JSON.parse(JSON.stringify(current ?? {})) as PreferencesData;
  const next = updater(currentCopy);
  return savePreferences(userId, next);
}
